import {
  CUSTOM_LIMITS,
  GameSession,
  MinesweeperClient,
  PRESET_IDS,
  formatDuration,
  validateBoardConfig,
} from '@p4p2r0/minesweeper-core';
import type { BoardConfig, DifficultyId, PresetId } from '@p4p2r0/minesweeper-core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { AccountPanel } from './components/AccountPanel';
import { BrandMark } from './components/BrandMark';
import { Drawer } from './components/Drawer';
import type { FaceMood } from './components/Face';
import { GameConsole } from './components/GameConsole';
import { Leaderboard } from './components/Leaderboard';
import { PersonIcon, TrophyIcon, TutorialIcon } from './components/NavIcons';
import { Toasts, type Toast } from './components/Toasts';
import { TutorialMode } from './components/TutorialMode';
import { useI18n, type StringKey } from './i18n';
import { useSound } from './sound';
import { HAPTICS, useFlagMode, usePerfMode, useTheme, vibrate } from './theme';
import {
  apiBaseUrl,
  createSubmissionStore,
  loadIdentity,
  saveIdentity,
  type Identity,
} from './storage';

type DrawerName = 'leaderboard' | 'account' | null;

/** How many notifications may stack up before the oldest non-sticky one goes. */
const MAX_TOASTS = 4;

/**
 * Tag shared by every "how did that game go" notification.
 *
 * The local outcome lands the instant the board is finished; the server's
 * verdict lands whenever the network manages it. One tag means the second
 * updates the first rather than queueing behind it.
 */
const OUTCOME_TAG = 'outcome';

const DIFFICULTY_KEYS: Record<PresetId, StringKey> = {
  beginner: 'difficulty.beginner',
  intermediate: 'difficulty.intermediate',
  expert: 'difficulty.expert',
};

export function App(): React.ReactElement {
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const { perf, setPerf } = usePerfMode();
  const { flagMode, setFlagMode } = useFlagMode();
  const { enabled: soundEnabled, setEnabled: setSoundEnabled, play: playSound } = useSound();
  const apiUrl = useMemo(apiBaseUrl, []);
  const [identity, setIdentity] = useState<Identity | null>(loadIdentity);
  const [customOpen, setCustomOpen] = useState(false);
  const [drawer, setDrawer] = useState<DrawerName>(null);
  // Swaps the whole stage into the walkthrough rather than opening it in a
  // drawer over the game — it needs the full board to actually teach on.
  // Toggles instantly, on purpose: no entrance/exit animation, so there's no
  // mount-delay dance needed to let one finish before unmounting.
  const [tutorialActive, setTutorialActive] = useState(false);
  const [pressing, setPressing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tick, setTick] = useState(() => Date.now());
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toastId = useRef(0);
  /**
   * Adds a notification.
   *
   * Two rules worth stating: a tagged toast *replaces* whichever one already
   * carries that tag, so "Cleared" turns into "Rank #4" in place when the
   * server answers instead of stacking a second card on top of it; and
   * trimming the stack never touches a sticky toast, because a sticky one
   * stands for something still unresolved — an unsaved win — and scrolling
   * that silently out of existence is how a win goes missing. Countdown and
   * dismissal live in `Toasts`, which can pause them on hover.
   */
  const pushToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = ++toastId.current;
    setToasts((list) => {
      const superseded = toast.tag ? list.filter((item) => item.tag !== toast.tag) : list;
      const next = [...superseded, { ...toast, id }];
      let surplus = next.length - MAX_TOASTS;
      if (surplus <= 0) return next;
      return next.filter((item) => {
        if (surplus <= 0 || item.sticky || item.id === id) return true;
        surplus--;
        return false;
      });
    });
  }, []);
  const dismissToast = useCallback((id: number) => {
    setToasts((list) => list.filter((item) => item.id !== id));
  }, []);
  const dismissByKind = useCallback((predicate: (toast: Toast) => boolean) => {
    setToasts((list) => list.filter((item) => !predicate(item)));
  }, []);

  // Always built, even without a key: anyone can read the leaderboards, and the
  // session decides on its own that a keyless client cannot post scores.
  const client = useMemo(
    () => new MinesweeperClient({ baseUrl: apiUrl, token: identity?.token ?? null }),
    [apiUrl, identity],
  );

  const session = useMemo(() => new GameSession({ client, store: createSubmissionStore() }), []);
  useEffect(() => {
    session.setClient(client);
  }, [session, client]);
  useEffect(() => () => session.dispose(), [session]);

  // Stable identities so `Board` (memoized — see its own comment on why)
  // doesn't reconcile its whole grid every 100ms tick just because App
  // re-rendered; only an actual game/session change should do that.
  const handleReveal = useCallback((x: number, y: number) => void session.reveal(x, y), [session]);
  const handleFlag = useCallback(
    (x: number, y: number) => {
      if (session.flag(x, y)) {
        vibrate(HAPTICS.flag);
        playSound('flag');
      }
    },
    [session, playSound],
  );
  const handleChord = useCallback((x: number, y: number) => session.chord(x, y), [session]);
  const handleNewBoard = useCallback(() => {
    setPressing(false);
    void session.replay();
  }, [session]);

  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const { game, phase } = snapshot;

  // Deal the opening board once. A ref rather than a phase check, because
  // StrictMode runs this effect twice before the phase has moved on.
  const dealt = useRef(false);
  useEffect(() => {
    if (dealt.current) return;
    dealt.current = true;
    void session.newGame('beginner');
  }, [session]);

  useEffect(() => {
    if (phase !== 'playing') return;
    const timer = window.setInterval(() => setTick(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, [phase]);

  // --- everything the game wants to say, said as a notification -------------

  const seenStatus = useRef(game.status);
  useEffect(() => {
    if (game.status === seenStatus.current) return;
    seenStatus.current = game.status;
    if (game.status === 'lost') {
      // A loss speaks for itself — the board shakes and the mine flashes red.
      // The whole point of a notification is telling you something you can't
      // already see; a "you lost" banner on top of a board visibly covered in
      // exploded mines isn't that. Sound plays regardless — it's feedback,
      // not a notification, and losing silently would feel like nothing
      // happened.
      vibrate(HAPTICS.lose);
      playSound('loss');
    } else if (game.status === 'won') {
      vibrate(HAPTICS.win);
      playSound('win');
      // Said now, from the local engine, not when the server gets round to
      // answering. A win used to wait on the round trip before showing
      // anything at all, so a slow connection meant clearing the board and
      // then sitting in front of it for the better part of a minute with no
      // acknowledgement that anything had happened. The time is already
      // known here, and it is the same time the server will confirm — so it
      // goes up immediately, and the verdict (rank, personal best, XP)
      // replaces this card in place whenever it lands. See OUTCOME_TAG.
      pushToast({
        kind: 'win',
        tag: OUTCOME_TAG,
        title: t('toast.cleared'),
        detail: snapshot.offline
          ? t('toast.notRanked')
          : `${formatDuration(game.elapsedMs)}s · ${t('toast.verifying')}`,
      });
    }
  }, [game.status, game.elapsedMs, snapshot.offline, pushToast, playSound, t]);

  const seenResult = useRef(snapshot.result);
  useEffect(() => {
    const result = snapshot.result;
    if (!result || result === seenResult.current) return;
    seenResult.current = result;
    setRefreshKey((value) => value + 1);
    // A win that had to be retried leaves a "still trying" toast behind it —
    // clear that out now that it actually landed.
    dismissByKind((toast) => toast.tag === 'pending-submission');
    const streakWorthMentioning =
      result.streakExtended && (result.currentStreak ?? 0) > 1
        ? t('toast.streak', { n: result.currentStreak ?? 0 })
        : null;
    const levelWorthMentioning = result.leveledUp
      ? t('toast.levelUp', { n: result.level ?? 0 })
      : null;
    if (result.leveledUp) playSound('levelUp');

    if (!result.won) {
      // A loss already speaks for itself on the board — only worth a toast
      // when it actually earned something.
      if (result.xpGained) {
        pushToast({
          kind: 'loss',
          tag: OUTCOME_TAG,
          title: t('toast.progressXp', { n: result.xpGained }),
          detail: levelWorthMentioning ?? undefined,
          emphasis: result.leveledUp,
        });
      }
      return;
    }

    // Shares OUTCOME_TAG with the local "Cleared" card pushed the moment the
    // board finished, so this updates that card rather than stacking beneath
    // it — the news here is the rank, not the win, which the player already
    // knows about.
    if (result.ranked && !result.timeRecorded) {
      // The win counted — games won, XP, the streak — but it reached the
      // server too long after it finished for the duration to be vouched
      // for, so it has no place on the time board. Saying so beats showing a
      // rank that was never awarded.
      pushToast({
        kind: 'win',
        tag: OUTCOME_TAG,
        title: t('toast.cleared'),
        detail: levelWorthMentioning ?? streakWorthMentioning ?? t('toast.notTimed'),
        emphasis: result.leveledUp,
      });
      return;
    }

    pushToast(
      result.ranked
        ? {
            kind: 'win',
            tag: OUTCOME_TAG,
            title: t('toast.rank', { rank: result.rank ?? 0 }),
            detail:
              levelWorthMentioning ??
              (result.personalBest ? t('toast.personalBest') : null) ??
              streakWorthMentioning ??
              `${formatDuration(result.elapsedMs)}s`,
            emphasis: result.personalBest || result.leveledUp,
          }
        : {
            kind: 'win',
            tag: OUTCOME_TAG,
            title: t('toast.cleared'),
            detail: t('toast.notRanked'),
          },
    );
  }, [snapshot.result, pushToast, dismissByKind, playSound, t]);

  // A win that could not reach the server gets its own sticky toast with a
  // retry button, rather than the ordinary auto-dismissing error toast — this
  // one should not be easy to lose track of, since it represents a real win
  // that has not been saved yet.
  const seenPending = useRef(false);
  useEffect(() => {
    if (snapshot.pendingSubmission === seenPending.current) return;
    seenPending.current = snapshot.pendingSubmission;
    if (snapshot.pendingSubmission) {
      pushToast({
        kind: 'error',
        title: t('toast.saveFailed'),
        detail: t('toast.willRetry'),
        sticky: true,
        tag: 'pending-submission',
        action: { label: t('toast.retryNow'), onClick: () => session.retrySubmission() },
      });
    } else {
      dismissByKind((toast) => toast.tag === 'pending-submission');
    }
  }, [snapshot.pendingSubmission, pushToast, dismissByKind, session, t]);

  // The moment connectivity comes back, try again — the player should not have
  // to notice the network dropped, remember there was a stuck win, and go find
  // a retry button for it.
  useEffect(() => {
    const onOnline = (): void => session.retrySubmission();
    window.addEventListener('online', onOnline);
    // Coming back to a backgrounded tab is the other moment the connection
    // has plausibly changed without an `online` event to announce it.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') session.retrySubmission();
    };
    document.addEventListener('visibilitychange', onVisible);
    // And once on mount, for anything left over from a previous visit.
    session.retrySubmission();
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [session]);

  const seenError = useRef(snapshot.error);
  useEffect(() => {
    const error = snapshot.error;
    if (!error || error === seenError.current) return;
    seenError.current = error;
    // The pending-submission case already has its own sticky toast above.
    if (snapshot.pendingSubmission) return;
    pushToast({ kind: 'error', title: error });
  }, [snapshot.error, snapshot.pendingSubmission, pushToast]);

  const changeIdentity = (next: Identity | null): void => {
    saveIdentity(next);
    setIdentity(next);
  };

  const start = (difficulty: DifficultyId, custom?: BoardConfig): void => {
    if (difficulty !== 'custom') setCustomOpen(false);
    setPressing(false);
    void session.newGame(difficulty, custom);
  };

  const over = game.status === 'won' || game.status === 'lost';
  const elapsedMs = over
    ? game.elapsedMs
    : snapshot.startedAt === null
      ? 0
      : Math.max(0, tick - snapshot.startedAt);

  const mood: FaceMood =
    game.status === 'won' ? 'won' : game.status === 'lost' ? 'lost' : pressing ? 'tense' : 'idle';

  return (
    <div className="app">
      {/* Three destinations, each icon+label on desktop and icon-only on a
          phone-width screen — narrow enough that even three labelled buttons
          were wrapping onto a second, cramped row. Theme and language moved
          into the Account drawer, which is where "preferences" belongs and
          which also gets the top bar back down to a single line. */}
      <header className="topbar">
        <div className="brand">
          <BrandMark className="brand-mark" />
          <span className="wordmark">Minesweeper</span>
        </div>

        <nav className="topnav">
          <button
            type="button"
            className={tutorialActive ? 'navlink active' : 'navlink'}
            aria-pressed={tutorialActive}
            onClick={() => setTutorialActive((active) => !active)}
          >
            <TutorialIcon />
            <span className="navlink-label">{t('nav.tutorial')}</span>
          </button>
          <button type="button" className="navlink" onClick={() => setDrawer('leaderboard')}>
            <TrophyIcon />
            <span className="navlink-label">{t('nav.leaderboard')}</span>
          </button>
          <button type="button" className="navlink" onClick={() => setDrawer('account')}>
            <PersonIcon />
            <span className="navlink-label">{identity ? identity.username : t('nav.account')}</span>
          </button>
        </nav>
      </header>

      <main className="stage">
        {tutorialActive ? (
          <div className="tutorial-live-wrap">
            <TutorialMode
              onDone={() => setTutorialActive(false)}
              flagMode={flagMode}
              onFlagModeChange={setFlagMode}
            />
          </div>
        ) : (
          <div className="game-stage">
            <div className="segmented" role="tablist" aria-label={t('board.difficulty')}>
              {PRESET_IDS.map((id: PresetId) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={snapshot.difficulty === id && !customOpen}
                  className={snapshot.difficulty === id && !customOpen ? 'segment active' : 'segment'}
                  onClick={() => start(id)}
                >
                  {t(DIFFICULTY_KEYS[id])}
                </button>
              ))}
              <button
                type="button"
                role="tab"
                aria-selected={customOpen}
                className={customOpen ? 'segment active' : 'segment'}
                onClick={() => setCustomOpen((open) => !open)}
              >
                {t('difficulty.custom')}
              </button>
            </div>

            {customOpen ? <CustomForm onStart={(config) => start('custom', config)} /> : null}

            <GameConsole
              game={game}
              elapsedMs={elapsedMs}
              mood={mood}
              disabled={over}
              onReveal={handleReveal}
              onFlag={handleFlag}
              onChord={handleChord}
              onPressChange={setPressing}
              onNewBoard={handleNewBoard}
              flagMode={flagMode}
              onFlagModeChange={setFlagMode}
            />

            <p className="hud-hint">{t(flagMode ? 'game.hintFlagMode' : 'game.hint')}</p>
          </div>
        )}
      </main>

      <Drawer
        open={drawer === 'leaderboard'}
        label={t('nav.leaderboard')}
        onClose={() => setDrawer(null)}
      >
        <Leaderboard client={client} refreshKey={refreshKey} />
      </Drawer>

      <Drawer open={drawer === 'account'} label={t('nav.account')} onClose={() => setDrawer(null)}>
        <AccountPanel
          apiUrl={apiUrl}
          identity={identity}
          onIdentity={changeIdentity}
          refreshKey={refreshKey}
          theme={theme}
          onTheme={setTheme}
          lang={lang}
          onLang={setLang}
          perf={perf}
          onPerf={setPerf}
          soundEnabled={soundEnabled}
          onSoundEnabled={setSoundEnabled}
        />
      </Drawer>

      <Toasts toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

function CustomForm({ onStart }: { onStart: (config: BoardConfig) => void }): React.ReactElement {
  const { t } = useI18n();
  const [width, setWidth] = useState('16');
  const [height, setHeight] = useState('16');
  const [mines, setMines] = useState('40');
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      className="custom-form"
      onSubmit={(event) => {
        event.preventDefault();
        const config: BoardConfig = {
          width: Number.parseInt(width, 10),
          height: Number.parseInt(height, 10),
          mines: Number.parseInt(mines, 10),
        };
        const check = validateBoardConfig(config);
        if (!check.ok) {
          setError(check.reason);
          return;
        }
        setError(null);
        onStart(config);
      }}
    >
      <label className="field">
        <span className="faint">{t('custom.width')}</span>
        <input
          type="number"
          value={width}
          min={CUSTOM_LIMITS.minWidth}
          max={CUSTOM_LIMITS.maxWidth}
          onChange={(event) => setWidth(event.target.value)}
        />
      </label>
      <label className="field">
        <span className="faint">{t('custom.height')}</span>
        <input
          type="number"
          value={height}
          min={CUSTOM_LIMITS.minHeight}
          max={CUSTOM_LIMITS.maxHeight}
          onChange={(event) => setHeight(event.target.value)}
        />
      </label>
      <label className="field">
        <span className="faint">{t('custom.mines')}</span>
        <input
          type="number"
          value={mines}
          min={CUSTOM_LIMITS.minMines}
          onChange={(event) => setMines(event.target.value)}
        />
      </label>
      <button type="submit" className="btn">
        {t('custom.set')}
      </button>
      {error ? <p className="error small">{error}</p> : null}
    </form>
  );
}
